import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import { Button, Input, Select, Skeleton, Tag, Typography } from "antd";
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import * as assetAction from "store/Actions/asset";
import * as portfolioAction from "store/Actions/portfolio";
import OverviewCard from "Components/Dashboard/OverviewCard";
import BinanceBalanceCard from "Components/Dashboard/BinanceBalanceCard";
import AllocationBar from "Components/Dashboard/AllocationBar";
import { getCoinInfo } from "utils/CoinGecko";
import AddAsset from "Components/Forms/AddAsset";
import Settings from "Components/Drawers/Settings";
import { mpTapAddCoinCTA, mpTapGoBack } from "Shared/Mixpanel/Assets";
import { mpTapRefreshCTA, mpTapViewMoreCTA } from "Shared/Mixpanel/Portfolios";
import { Tokens } from "Shared/Tokens";

const { Text } = Typography;
const { Option } = Select;

const FILTER_CHIPS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "gainers", label: "Gainers" },
    { key: "losers", label: "Losers" },
    { key: "exited", label: "Exited" },
];

const Assets = () => {
    const history = useHistory();
    const { search } = useLocation();
    const { name: routePortfolioName } = useParams() || {};
    const intent = new URLSearchParams(search).get("intent");
    const dispatch = useDispatch();
    const portfolios = useSelector(
        (state) => state.portfolios?.portfolios || []
    );
    const selectedPortfolio = useSelector(
        (state) => state.portfolios.selectedPortfolio
    ) || { name: "Portfolio", uid: "" };
    const settings = useSelector((state) => state.settings || {});
    const {
        defaultSortBy,
        hideSmallBalances = false,
        hideExitedPositions = false,
    } = settings;
    const displayCurrency = settings?.currency || "INR";

    document.title = `${selectedPortfolio.name} — Portfolio`;
    const assets = useSelector((state) => state.assets.assets) || [];

    const [items, setItems] = useState([]);
    const [showAddAssetDrawer, setShowAddAssetDrawer] = useState(
        intent === "add"
    );
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState(
        defaultSortBy || settings.binanceSortBy || "currentValue"
    );
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [showSettings, setShowSettings] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!routePortfolioName || !Array.isArray(portfolios)) return;
        const decodedName = decodeURIComponent(routePortfolioName);
        if (
            !selectedPortfolio?.uid ||
            (selectedPortfolio.name !== decodedName &&
                selectedPortfolio.uid !== decodedName)
        ) {
            const matched = portfolios.find(
                (p) => p.name === decodedName || p.uid === decodedName
            );
            if (matched) {
                dispatch(portfolioAction.setSelectedPortfolio(matched));
            }
        }
    }, [routePortfolioName, portfolios, selectedPortfolio, dispatch]);

    useEffect(() => {
        const nextSort = defaultSortBy || settings?.binanceSortBy;
        if (nextSort) {
            setSortBy(nextSort);
        }
    }, [defaultSortBy, settings?.binanceSortBy]);

    useEffect(() => {
        dispatch(assetAction.setPortfolioAssets(selectedPortfolio));
    }, [dispatch, selectedPortfolio]);

    const handleMoreClk = (uid) => {
        const asset = assets.find((item) => item.uid === uid);
        if (!asset) return;
        mpTapViewMoreCTA({ name: asset.name });
        dispatch(assetAction.setSelectedAsset(asset));
        history.push(
            `/portfolios/${selectedPortfolio.name}/assets/${asset.id}`
        );
    };

    const deleteThisPortfolio = () => {
        dispatch(portfolioAction.deletePortfolio(selectedPortfolio));
        history.push("/portfolios");
    };

    const getAssetInfo = async () => {
        setLoading(true);
        const currentPortfolioAssets = assets.filter(
            (asset) => asset.portfolioId === selectedPortfolio.uid
        );
        const coinsMarketData =
            currentPortfolioAssets.length > 0
                ? await getCoinInfo(currentPortfolioAssets, displayCurrency)
                : [];

        const computedCoins = currentPortfolioAssets.map((asset) => {
            const coinInfo = (coinsMarketData || []).find(
                (data) => data.id === asset.id
            );
            const txs = Array.isArray(asset.transactions)
                ? asset.transactions
                : [];

            let lifetimeBoughtCost = 0;
            let lifetimeBoughtCoins = 0;
            let lifetimeSoldProceeds = 0;
            let lifetimeSoldCoins = 0;

            txs.forEach((tx) => {
                const snap = tx?.portfolioSnapshot || tx || {};
                const prev = snap.previous || tx?.previous || {};
                const next = snap.latest || tx?.latest || {};
                const prevCoins = Number(prev?.numberOfCoins || 0);
                const nextCoins = Number(next?.numberOfCoins || 0);
                const coinDelta = Math.abs(nextCoins - prevCoins);
                const mktPrice = Number(
                    snap.transactionMarketPrice ??
                        tx?.transactionMarketPrice ??
                        0
                );
                const invDelta = Math.abs(
                    Number(next?.investment || 0) -
                        Number(prev?.investment || 0)
                );
                const txUsdVal =
                    coinDelta * mktPrice > 0 ? coinDelta * mktPrice : invDelta;

                if (
                    tx?.type === "buy" ||
                    (tx?.type !== "sell" && nextCoins > prevCoins)
                ) {
                    lifetimeBoughtCost += txUsdVal;
                    lifetimeBoughtCoins += coinDelta;
                } else if (
                    tx?.type === "sell" ||
                    (tx?.type !== "buy" && nextCoins < prevCoins)
                ) {
                    const soldProceeds =
                        Number(next?.sellInfo?.amountToSell) || txUsdVal;
                    lifetimeSoldProceeds += soldProceeds;
                    lifetimeSoldCoins += coinDelta;
                }
            });

            const quantity = Number(
                asset.portfolio?.latest?.numberOfCoins || 0
            );
            const isActive = quantity > 1e-10;
            const totalInvestment = isActive
                ? Number(asset.portfolio?.latest?.investment || 0)
                : 0;
            const currentPrice = Number(coinInfo?.current_price || 0);
            const currentValue = isActive ? quantity * currentPrice : 0;
            const avgPrice = isActive ? totalInvestment / quantity : 0;

            const isExited =
                !isActive &&
                (txs.length > 0 ||
                    lifetimeBoughtCost > 0 ||
                    Number(asset.totalBoughtCost) > 0);

            const plValue = currentValue - totalInvestment;
            const pl =
                totalInvestment > 0
                    ? ((currentValue - totalInvestment) / totalInvestment) * 100
                    : 0;

            const lifetimeInvested =
                Number(asset.totalBoughtCost) > 0
                    ? Number(asset.totalBoughtCost)
                    : lifetimeBoughtCost;

            let realizedPL = 0;
            if (Number.isFinite(Number(asset.realizedPL))) {
                realizedPL = Number(asset.realizedPL);
            } else if (isExited) {
                realizedPL = lifetimeSoldProceeds - lifetimeInvested;
            }

            const realizedRoiPct =
                lifetimeInvested > 0
                    ? (realizedPL / lifetimeInvested) * 100
                    : 0;
            const avgExitPrice =
                lifetimeSoldCoins > 0
                    ? lifetimeSoldProceeds / lifetimeSoldCoins
                    : 0;

            return {
                ...asset,
                symbol: asset.symbol || coinInfo?.symbol || "",
                avatar: coinInfo?.image || asset.avatar || "",
                quantity,
                currentPrice,
                currentValue,
                totalInvestment,
                avgPrice,
                pl,
                plValue,
                isExited,
                realizedPL,
                realizedRoiPct,
                lifetimeInvested,
                lifetimeBoughtCoins,
                totalSold: lifetimeSoldProceeds,
                avgExitPrice,
                tradeCount: txs.length,
            };
        });

        const totalPortfolioVal = computedCoins.reduce(
            (sum, coin) => sum + (coin.isExited ? 0 : coin.currentValue),
            0
        );
        const totalInvestedVal = computedCoins.reduce(
            (sum, coin) => sum + (coin.isExited ? 0 : coin.totalInvestment),
            0
        );

        const enrichedCoins = computedCoins.map((coin) => {
            let shareRatio = 0;
            if (!coin.isExited) {
                if (totalPortfolioVal > 0) {
                    shareRatio = Number(
                        (coin.currentValue / totalPortfolioVal).toFixed(2)
                    );
                } else if (totalInvestedVal > 0) {
                    shareRatio = Number(
                        (coin.totalInvestment / totalInvestedVal).toFixed(2)
                    );
                }
            }
            return {
                ...coin,
                investmentPie: shareRatio,
            };
        });

        setItems(enrichedCoins);
        setLoading(false);
    };

    useEffect(() => {
        getAssetInfo();
        // eslint-disable-next-line
    }, [JSON.stringify(assets), displayCurrency, selectedPortfolio?.uid]);

    const activeAssetItems = items.filter(
        (item) => !item.isExited && item.quantity > 1e-10
    );
    const exitedAssetItems = items.filter((item) => item.isExited);

    const activeCount = activeAssetItems.length;
    const exitedCount = exitedAssetItems.length;

    const totalPortfolioValue = activeAssetItems.reduce(
        (sum, item) => sum + (Number(item.currentValue) || 0),
        0
    );
    const currentInvestmentAmount = activeAssetItems.reduce(
        (sum, item) => sum + (Number(item.totalInvestment) || 0),
        0
    );
    const totalPortfolioRealizedPL = items.reduce(
        (sum, item) =>
            sum +
            (Number.isFinite(Number(item.realizedPL))
                ? Number(item.realizedPL)
                : 0),
        0
    );

    const filteredAndSortedItems = items
        .filter((item) => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const nameMatch = (item.name || "").toLowerCase().includes(q);
                const symMatch = (item.symbol || "").toLowerCase().includes(q);
                if (!nameMatch && !symMatch) return false;
            }
            if (
                hideSmallBalances &&
                !item.isExited &&
                (item.currentValue || 0) < 1.0
            ) {
                return false;
            }
            if (
                hideExitedPositions &&
                categoryFilter === "all" &&
                item.isExited
            ) {
                return false;
            }
            if (categoryFilter === "active") {
                return !item.isExited;
            }
            if (categoryFilter === "exited") {
                return item.isExited;
            }
            if (categoryFilter === "gainers") {
                return item.isExited ? item.realizedPL > 0 : item.pl > 0;
            }
            if (categoryFilter === "losers") {
                return item.isExited ? item.realizedPL < 0 : item.pl < 0;
            }
            return true;
        })
        .sort((a, b) => {
            if (a.isExited !== b.isExited && categoryFilter === "all") {
                return a.isExited ? 1 : -1;
            }
            if (sortBy === "currentValue") {
                if (a.isExited && b.isExited) {
                    return (b.realizedPL || 0) - (a.realizedPL || 0);
                }
                return (b.currentValue || 0) - (a.currentValue || 0);
            }
            if (sortBy === "investment") {
                const invA = a.isExited
                    ? a.lifetimeInvested || 0
                    : a.totalInvestment || 0;
                const invB = b.isExited
                    ? b.lifetimeInvested || 0
                    : b.totalInvestment || 0;
                return invB - invA;
            }
            if (sortBy === "roi") {
                const roiA = a.isExited ? a.realizedRoiPct || 0 : a.pl || 0;
                const roiB = b.isExited ? b.realizedRoiPct || 0 : b.pl || 0;
                return roiB - roiA;
            }
            if (sortBy === "name") {
                return (a.name || "").localeCompare(b.name || "");
            }
            return 0;
        });

    return (
        <div>
            <AppShell
                activeTab="overview"
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: 0,
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ArrowLeftOutlined
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={() => {
                                    mpTapGoBack({
                                        screen: "ALL_ASSETS_SCREEN",
                                    });
                                    history.push("/portfolios");
                                }}
                                title="Back to portfolios"
                                style={{
                                    padding: 0,
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            />
                            <Typography.Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: 0,
                                    fontWeight: 500,
                                    fontSize: "18px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {selectedPortfolio.name}
                            </Typography.Title>
                            <AutoSyncBadge />
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ReloadOutlined
                                        spin={loading}
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={() => {
                                    mpTapRefreshCTA({ content: "Assets" });
                                    getAssetInfo();
                                }}
                                title="Refresh assets"
                                style={{
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusControl,
                                    width: "36px",
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            />
                            {confirmDelete ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <Button
                                        size="small"
                                        danger
                                        type="primary"
                                        data-testid="confirm-delete-portfolio-btn"
                                        onClick={deleteThisPortfolio}
                                        style={{
                                            height: "36px",
                                            borderRadius: Tokens.radiusControl,
                                            fontWeight: 500,
                                            fontSize: "12px",
                                        }}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        size="small"
                                        data-testid="cancel-delete-portfolio-btn"
                                        onClick={() => setConfirmDelete(false)}
                                        style={{
                                            height: "36px",
                                            borderRadius: Tokens.radiusControl,
                                            backgroundColor: Tokens.raised,
                                            borderColor: Tokens.hairline,
                                            color: Tokens.textSecondary,
                                            fontSize: "12px",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    type="text"
                                    danger
                                    data-testid="delete-portfolio-btn"
                                    icon={<DeleteOutlined />}
                                    title="Delete portfolio"
                                    onClick={() => setConfirmDelete(true)}
                                    style={{
                                        backgroundColor: Tokens.raised,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusControl,
                                        width: "36px",
                                        height: "36px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                />
                            )}
                        </div>
                    </div>
                }
                placement="right"
                closable={false}
                push={false}
                height="100%"
                width="100%"
                open
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "16px 24px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px 80px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))",
                    textAlign: "left",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "2px",
                                minWidth: 0,
                            }}
                        >
                            <span
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "11px",
                                    lineHeight: "1.2",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Holdings
                            </span>
                            <Text
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    lineHeight: "1.3",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {activeCount} active · {exitedCount} exited
                            </Text>
                        </div>

                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                mpTapAddCoinCTA();
                                setShowAddAssetDrawer(true);
                            }}
                            style={{
                                borderRadius: Tokens.radiusControl,
                                fontWeight: 500,
                                fontSize: "13px",
                                height: "38px",
                                padding: "0 16px",
                                flexShrink: 0,
                            }}
                        >
                            Add coin
                        </Button>
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    {loading && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                            }}
                        >
                            {[1, 2, 3, 4].map((index) => (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        padding: "20px",
                                    }}
                                >
                                    <Skeleton active paragraph={{ rows: 1 }} />
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && items.length > 0 && (
                        <React.Fragment>
                            <BinanceBalanceCard
                                label="Portfolio balance"
                                badgeText="Manual portfolio"
                                showLiveDot={false}
                                current={totalPortfolioValue}
                                invested={currentInvestmentAmount}
                                netPl={
                                    totalPortfolioValue -
                                    currentInvestmentAmount
                                }
                                realizedPl={totalPortfolioRealizedPL}
                                roi={
                                    currentInvestmentAmount > 0
                                        ? ((totalPortfolioValue -
                                              currentInvestmentAmount) /
                                              currentInvestmentAmount) *
                                          100
                                        : 0
                                }
                                currency={displayCurrency}
                            />

                            <AllocationBar
                                items={activeAssetItems}
                                total={totalPortfolioValue}
                            />

                            <div style={{ marginBottom: "16px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        marginBottom: "12px",
                                    }}
                                >
                                    <Input
                                        prefix={
                                            <SearchOutlined
                                                style={{
                                                    color: Tokens.textMuted,
                                                }}
                                            />
                                        }
                                        placeholder="Search coins"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        style={{
                                            flex: 1,
                                            backgroundColor: Tokens.card,
                                            borderColor: Tokens.hairline,
                                            color: Tokens.textPrimary,
                                            borderRadius: Tokens.radiusControl,
                                            height: "40px",
                                        }}
                                    />
                                    <Select
                                        value={sortBy}
                                        onChange={setSortBy}
                                        style={{
                                            width: "148px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Option value="currentValue">
                                            Current value
                                        </Option>
                                        <Option value="investment">
                                            Invested
                                        </Option>
                                        <Option value="roi">ROI %</Option>
                                        <Option value="name">Name</Option>
                                    </Select>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        overflowX: "auto",
                                        paddingBottom: "4px",
                                    }}
                                >
                                    {FILTER_CHIPS.map((chip) => {
                                        const isActive =
                                            categoryFilter === chip.key;
                                        return (
                                            <Tag
                                                key={chip.key}
                                                onClick={() =>
                                                    setCategoryFilter(chip.key)
                                                }
                                                style={{
                                                    backgroundColor: isActive
                                                        ? Tokens.raised
                                                        : "transparent",
                                                    color: isActive
                                                        ? Tokens.textPrimary
                                                        : Tokens.textSecondary,
                                                    border: isActive
                                                        ? `1px solid ${Tokens.accent}`
                                                        : `1px solid ${Tokens.hairline}`,
                                                    borderRadius:
                                                        Tokens.radiusTag,
                                                    padding: "4px 12px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: isActive
                                                        ? 500
                                                        : 400,
                                                    userSelect: "none",
                                                    margin: 0,
                                                }}
                                            >
                                                {chip.label}
                                            </Tag>
                                        );
                                    })}
                                </div>
                            </div>

                            {filteredAndSortedItems.length > 0 ? (
                                filteredAndSortedItems.map((coin, index) => (
                                    <OverviewCard
                                        itemInfo={{
                                            name: coin.name,
                                            symbol: coin.symbol,
                                            uid: coin.uid,
                                            avatar: coin.avatar,
                                            handleMoreClk,
                                        }}
                                        itemData={{
                                            isAssetCard: true,
                                            isExited: coin.isExited,
                                            quantity: coin.quantity,
                                            currentPrice: coin.currentPrice,
                                            currentValue: coin.currentValue,
                                            totalInvestment:
                                                coin.totalInvestment,
                                            avgPrice: coin.avgPrice,
                                            pl: coin.pl,
                                            plValue: coin.plValue,
                                            realizedPL: coin.realizedPL,
                                            realizedRoiPct: coin.realizedRoiPct,
                                            lifetimeInvested:
                                                coin.lifetimeInvested,
                                            totalSold: coin.totalSold,
                                            avgExitPrice: coin.avgExitPrice,
                                            tradeCount: coin.tradeCount,
                                            investmentPie: coin.investmentPie,
                                        }}
                                        currency={displayCurrency}
                                        index={index}
                                        key={coin.uid}
                                    />
                                ))
                            ) : (
                                <div
                                    style={{
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        padding: "24px",
                                        textAlign: "center",
                                        color: Tokens.textSecondary,
                                        fontSize: "13px",
                                    }}
                                >
                                    No coins match the current filter.
                                </div>
                            )}
                        </React.Fragment>
                    )}

                    {!loading && items.length === 0 && (
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "32px 24px",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                No coins added yet
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "14px",
                                }}
                            >
                                Add a coin to track holdings and cost basis in{" "}
                                {selectedPortfolio.name}.
                            </div>
                        </div>
                    )}
                </div>
            </AppShell>

            <AddAsset
                visible={showAddAssetDrawer}
                onClose={() => setShowAddAssetDrawer(false)}
            />
            <Settings
                visible={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </div>
    );
};

export default Assets;
